(function () {
  if (customElements.get('vant-model-filter')) return;

  class VantModelFilter extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();

      requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    initialize() {
      if (!this.isConnected) return;

      this.section = this.closest('.shopify-section');
      this.buttons = Array.from(this.querySelectorAll('[data-vant-model-button]'));
      this.status = this.querySelector('[data-vant-model-status]');
      this.empty = this.section?.querySelector('[data-vant-model-empty]');
      this.pagination = this.section?.querySelector('[data-vant-model-pagination]');
      this.defaultModel = this.dataset.defaultModel || 'all';
      this.urlParam = this.dataset.urlParam || 'modelagem';
      this.pageParam = this.dataset.pageParam || 'modelagem_pagina';
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 48);

      if (!this.section || this.buttons.length === 0) return;

      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => this.select(button.dataset.vantModelButton), {
          signal: this.abortController.signal,
        });

        button.addEventListener(
          'keydown',
          (event) => this.handleKeydown(event, index),
          { signal: this.abortController.signal }
        );
      });

      const currentUrl = new URL(window.location.href);

      if (currentUrl.searchParams.has('page')) {
        currentUrl.searchParams.delete('page');
        window.location.replace(currentUrl.toString());
        return;
      }

      const requestedModel = currentUrl.searchParams.get(this.urlParam);
      const requestedPage = Number(currentUrl.searchParams.get(this.pageParam));

      this.currentPage = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
      this.apply(this.isValidModel(requestedModel) ? requestedModel : this.defaultModel, false);
    }

    get cards() {
      return Array.from(this.section?.querySelectorAll('product-card[data-vant-model]') || []);
    }

    isValidModel(model) {
      return this.buttons.some((button) => button.dataset.vantModelButton === model);
    }

    handleKeydown(event, index) {
      let nextIndex = index;

      if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.buttons.length;
      else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.buttons.length) % this.buttons.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = this.buttons.length - 1;
      else return;

      event.preventDefault();
      this.buttons[nextIndex].focus();
      this.select(this.buttons[nextIndex].dataset.vantModelButton);
    }

    select(model) {
      if (!this.isValidModel(model)) model = this.defaultModel;

      this.currentPage = 1;
      this.updateUrl(model);
      this.apply(model, false);
    }

    apply(model, shouldScrollGrid) {
      const cards = this.cards;
      const matchingCards = cards.filter(
        (card) => model === 'all' || card.dataset.vantModel === model
      );
      const totalPages = Math.max(1, Math.ceil(matchingCards.length / this.pageSize));

      this.currentPage = Math.min(Math.max(this.currentPage || 1, 1), totalPages);

      const pageStart = (this.currentPage - 1) * this.pageSize;
      const visibleCards = new Set(matchingCards.slice(pageStart, pageStart + this.pageSize));

      this.buttons.forEach((button) => {
        const active = button.dataset.vantModelButton === model;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
      });

      cards.forEach((card) => {
        card.hidden = !visibleCards.has(card);
      });

      if (this.status) {
        const suffix = matchingCards.length === 1 ? 'produto encontrado' : 'produtos encontrados';
        this.status.textContent = `${matchingCards.length} ${suffix}`;
      }

      this.empty?.classList.toggle('is-visible', cards.length > 0 && matchingCards.length === 0);
      this.renderPagination(model, matchingCards.length);
      this.syncFacetForms(model);

      const productList = this.section.querySelector('.product-list');
      productList?.setAttribute('data-active-model', model);

      if (shouldScrollGrid) {
        productList?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    updateUrl(model) {
      const url = new URL(window.location.href);

      if (model === this.defaultModel) url.searchParams.delete(this.urlParam);
      else url.searchParams.set(this.urlParam, model);

      if (this.currentPage > 1) url.searchParams.set(this.pageParam, String(this.currentPage));
      else url.searchParams.delete(this.pageParam);

      url.searchParams.delete('page');
      window.history.replaceState({}, '', url.toString());
    }

    pageUrl(model, page) {
      const url = new URL(window.location.href);

      if (model === this.defaultModel) url.searchParams.delete(this.urlParam);
      else url.searchParams.set(this.urlParam, model);

      if (page > 1) url.searchParams.set(this.pageParam, String(page));
      else url.searchParams.delete(this.pageParam);

      url.searchParams.delete('page');
      return url.toString();
    }

    renderPagination(model, totalCount) {
      if (!this.pagination) return;

      this.pagination.replaceChildren();

      const totalPages = Math.ceil(totalCount / this.pageSize);
      if (totalPages <= 1) return;

      const nav = document.createElement('nav');
      nav.className = 'pagination';
      nav.setAttribute('role', 'navigation');
      nav.setAttribute('aria-label', 'Paginação dos produtos');

      const appendLink = (page, content, label, rel) => {
        const link = document.createElement('a');
        link.className = 'pagination__link h6';
        link.href = this.pageUrl(model, page);
        link.textContent = content;
        link.setAttribute('aria-label', label);
        if (rel) link.rel = rel;

        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.currentPage = page;
          this.updateUrl(model);
          this.apply(model, true);
        });

        nav.appendChild(link);
      };

      if (this.currentPage > 1) {
        appendLink(this.currentPage - 1, '‹', `Ir para a página ${this.currentPage - 1}`, 'prev');
      }

      const visiblePageNumbers = new Set([
        1,
        totalPages,
        this.currentPage - 1,
        this.currentPage,
        this.currentPage + 1,
      ]);
      const pageNumbers = Array.from(visiblePageNumbers)
        .filter((page) => page >= 1 && page <= totalPages)
        .sort((a, b) => a - b);

      pageNumbers.forEach((page, index) => {
        if (index > 0 && page - pageNumbers[index - 1] > 1) {
          const ellipsis = document.createElement('span');
          ellipsis.className = 'pagination__link pagination__link--disabled h6';
          ellipsis.textContent = '…';
          nav.appendChild(ellipsis);
        }

        if (page === this.currentPage) {
          const current = document.createElement('span');
          current.className = 'pagination__link pagination__link--disabled h6';
          current.textContent = String(page);
          current.setAttribute('aria-current', 'page');
          nav.appendChild(current);
        } else {
          appendLink(page, String(page), `Ir para a página ${page}`);
        }
      });

      if (this.currentPage < totalPages) {
        appendLink(this.currentPage + 1, '›', `Ir para a página ${this.currentPage + 1}`, 'next');
      }

      this.pagination.appendChild(nav);
    }

    syncFacetForms(model) {
      this.section.querySelectorAll('form[is="facets-form"]').forEach((form) => {
        let input = form.querySelector(`input[data-vant-model-param="${this.urlParam}"]`);

        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = this.urlParam;
          input.dataset.vantModelParam = this.urlParam;
          form.appendChild(input);
        }

        input.value = model === this.defaultModel ? '' : model;
        input.disabled = model === this.defaultModel;
      });
    }
  }

  customElements.define('vant-model-filter', VantModelFilter);
})();
